import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { log } from '@src/Log.init';
import CustomInput from 'components/CustomInput/CustomInput.js';
import { useState } from 'react';
import { WarningAlert } from 'views/Components/SweetAlert';

import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import { useUpdateReferenceListMutation } from 'api/reference-list.api';
import { useAvailableLanguages } from 'hooks/useAvailableLanguages';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 480,
  },
}));

const LanguageLabels = ({ entryLabels, setEntryLabels, classes }) => {
  const updateEntryLabel = (label, entryIndex) => {
    const updatedLabel = {
      language: entryLabels[entryIndex].language,
      label: label,
    };
    const updatedLabels = [...entryLabels];
    updatedLabels.splice(entryIndex, 1, updatedLabel);
    setEntryLabels(updatedLabels);
  };

  return entryLabels.map((languageEntry, key) => (
    <Grid item xs={12} key={key}>
      <CustomInput
        key={'label-' + key}
        className={classes.formControl}
        margin='dense'
        id={'label-' + key}
        labelText={languageEntry.language}
        inputProps={{
          className: classes.formControl,
          defaultValue: languageEntry.label,
          onChange: (e) => updateEntryLabel(e.target.value, key),
        }}
      />
    </Grid>
  ));
};

// we need the entry to edit, the list of supported languages for new entries and a callback to save the entry
export default function ReferenceListEntry(props) {
  const supportedLanguages = useAvailableLanguages();

  const classes = useStyles();

  const buildDefaultEntry = () => {
    const defaultEntry = { entry_code: '', entry_label: [] };
    supportedLanguages?.map((language) =>
      defaultEntry.entry_label.push({ language: language, label: '' })
    );
    return defaultEntry;
  };

  // flesh out the label list to ensure all supported languages appear
  const buildFullEntry = (currentEntry) => {
    const fullEntry = { entry_code: currentEntry.entry_code, entry_label: [] };
    supportedLanguages?.map((supportedLanguage) => {
      const labelEntry = currentEntry.entry_label.find(
        ({ language }) => language === supportedLanguage
      );
      const label =
        labelEntry === undefined || labelEntry.label === undefined
          ? ''
          : labelEntry.label;
      fullEntry.entry_label.push({ language: supportedLanguage, label: label });
      return fullEntry;
    });
    return fullEntry;
  };

  const listEntry = props.entry
    ? buildFullEntry(props.entry)
    : buildDefaultEntry();

  const [entryCode, setEntryCode] = useState(listEntry.entry_code);
  const [entryLabels, setEntryLabels] = useState(listEntry.entry_label);
  const [alert, setAlert] = useState({ show: false, message: '', title: '' });

  const [updateReferenceList] = useUpdateReferenceListMutation();

  const title = props.new ? 'Enter New List Entry' : 'Edit List Entry';
  const submitLabel = props.new ? 'Create' : 'Save Changes';

  // eslint-disable-next-line
  const handleUpdate = (e) => {
    e.preventDefault();
    // build updated entry
    const entryData = { entry_code: entryCode, entry_label: [...entryLabels] };
    // replace updated entry in list
    const updatedList = { ...props.list };
    const updatedLabelList = [...props.list.list];
    updatedLabelList.splice(props.index, 1, entryData);
    updatedList.list = updatedLabelList;

    updateReferenceList(updatedList)
      .unwrap()
      .catch((error) => {
        log.debug({
          message_type: 'ReferenceLists',
          message: 'Error updating reference list',
          meta_data: error,
        });
      });

    props.handleClose();
  };

  // eslint-disable-next-line
  const handleAdd = (e) => {
    e.preventDefault();

    if (
      props.list?.list?.find(({ entry_code }) => entry_code === entryCode) !==
      undefined
    ) {
      setAlert({
        show: true,
        message: 'Entry code already exists. Enter a different code.',
        title: 'Duplicated entry code!',
      });
      return;
    }

    // build new entry
    const entryData = { entry_code: entryCode, entry_label: [...entryLabels] };
    // add new entry to list
    const updatedList = {
      ...props.list,
      list: [...props.list.list, entryData],
    };

    updateReferenceList(updatedList)
      .unwrap()
      .catch((error) => {
        log.debug({
          message_type: 'ReferenceLists',
          message: 'Error updating reference list',
          meta_data: error,
        });
      });

    props.handleClose();
  };

  const handleSubmit = props.new ? handleAdd : handleUpdate;

  return (
    <div>
      {alert.show && (
        <WarningAlert
          message={alert.message}
          title={alert.title}
          onConfirm={() => setAlert({ show: false, message: '', title: '' })}
        />
      )}
      <Dialog
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>{title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomInput
                autoFocus
                margin='dense'
                id='code'
                labelText='Entry code'
                inputProps={{
                  className: classes.formControl,
                  disabled: !props.new,
                  defaultValue: entryCode,
                  onChange: (e) => setEntryCode(e.target.value),
                }}
              />
            </Grid>
            <LanguageLabels
              entryLabels={entryLabels}
              setEntryLabels={setEntryLabels}
              classes={classes}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={props.handleClose} color='secondary'>
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            onClick={handleSubmit}
            disabled={
              entryCode === '' ||
              entryLabels.every((label) => label.label === '')
            }
            color='primary'
          >
            {submitLabel}
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
